import { apiPost, type Envelope } from "./client.js";
import type { Role } from "../roles.js";

/**
 * NX-2633 (E8.8) — the what-if scenario service: the only place the WhatIfSimulator panel learns
 * how to reach `POST /projects/:id/scenarios` (apps/api/src/scenario.ts, NX-2637/E7.5 — shipped on
 * main, see this story's report). The panel and its lever components take a `ScenarioService` and
 * never call `apiPost`/`fetch` themselves (AC5's "no panel does its own inline fetch").
 *
 * Shapes mirror apps/api/src/scenario.ts and packages/core/src/scenario-engine.ts rather than being
 * imported: those carry Node-only code (crypto, pg), so the browser bundle never imports them — the
 * same rule `assessmentService.ts`/`roles.ts` document. `scenarioService.test.ts` binds the role
 * list below to the server's own `SCENARIO_POLICY.assumptions`, and the registered lever ids to the
 * real `ThroughputLever`/`FrictionLever` classes, in a Node-environment test, so neither can drift
 * silently.
 */

// --- mirrored shapes ------------------------------------------------------------------------------

export type Metric<T> =
  | { readonly kind: "n/a"; readonly reason: string }
  | {
      readonly kind: "value";
      readonly value: T;
      readonly confidence:
        | { readonly kind: "n/a" }
        | { readonly kind: "value"; readonly confidence: number };
      readonly lineage: readonly number[];
    };

/** Mirrors packages/core/src/throughput-forecast.ts's `ThroughputForecastValue` — the same shape
 *  `GET /projects/:id`'s own `forecast` field carries (Project.tsx's local, unexported
 *  `ThroughputForecastValue`). Redeclared here rather than imported across directories, matching
 *  this file's own self-contained-mirror convention. */
export interface ThroughputForecastValue {
  readonly modelVersion: string;
  readonly remainingItems: number;
  readonly activePeriods: number;
  readonly trials: number;
  readonly seed: number;
  readonly p50: number;
  readonly p85Raw: number;
  readonly p85Adjusted: number;
  readonly p95: number;
  readonly bandMultiplier: number;
}

export interface ScopeAtDateValue {
  readonly modelVersion: string;
  readonly targetDate: string;
  readonly periodsAvailable: number;
  readonly remainingItems: number;
  readonly activePeriods: number;
  readonly trials: number;
  readonly seed: number;
  readonly scopeAtP50: number;
  readonly scopeAtP85: number;
  readonly scopeAtP15: number;
  readonly bandMultiplier: number;
}

export type DateModeForecast = {
  readonly mode: "date-driven";
  readonly forecast: Metric<ScopeAtDateValue>;
};

export type ScenarioAssumptionBasis = "own-data" | "cohort-benchmark";

/** Mirrors packages/core/src/scenario-engine.ts's `ScenarioAssumption`. `basis` distinguishes
 *  own-data from cohort-benchmark — NOT the same confidence, and the UI must not present them as
 *  such (this story's own directive; see WhatIfLevers.tsx's `AssumptionBasisTag`). */
export interface ScenarioAssumptionView {
  readonly leverId: string;
  readonly magnitude: number;
  readonly basis: ScenarioAssumptionBasis;
  readonly rationale: string;
  readonly confidence:
    | { readonly kind: "n/a" }
    | { readonly kind: "value"; readonly confidence: number };
}

/** The masked shape `SCENARIO_POLICY` (scenario.ts) produces over the wire: `assumptions` is
 *  ABSENT (not null) for a role outside `SCENARIO_LEVER_ROLES` below — mirrors
 *  `CapabilityAssessmentView`'s identical `responder?:` masking convention. */
export interface LegacyScenarioView {
  readonly mode?: never;
  readonly baseline: Metric<ThroughputForecastValue>;
  readonly scenario: Metric<ThroughputForecastValue>;
  readonly assumptions?: readonly ScenarioAssumptionView[];
}
export interface DateScenarioView {
  readonly mode: "date-driven";
  readonly baseline: DateModeForecast;
  readonly scenario: DateModeForecast;
  readonly assumptions?: readonly ScenarioAssumptionView[];
}
export type ScenarioView = LegacyScenarioView | DateScenarioView;

// --- registered backend levers (apps/api/src/index.ts's scenarioLevers() / scenario-repository.ts) -

/** The registered lever ids the production `ScenarioEngine` registry currently wires. */
export const REGISTERED_SCENARIO_LEVER_IDS = [
  "throughput-adjustment",
  "friction-reduction",
  "wip-limit-tightening",
  "trade-scope-at-date",
] as const;
export type RegisteredScenarioLeverId = (typeof REGISTERED_SCENARIO_LEVER_IDS)[number];

// --- roles ------------------------------------------------------------------------------------

/**
 * Mirrors scenario.ts's `EVIDENCE_ROLES`, exposed there as `SCENARIO_POLICY.assumptions` — AC7's
 * "delivery-side roles" (admin/delivery-lead/sponsor). The client gate this drives is a UI courtesy
 * exactly like `ASSESSMENT_SUBMIT_ROLES` (assessmentService.ts): it hides the lever controls so a
 * client-viewer/readonly role sees the read-only ribbon and no control that looks actionable. It
 * does NOT replace server-side enforcement — `POST /projects/:id/scenarios` currently has no
 * `requireRole` call at all (verified by direct inspection of scenario.ts), so any authenticated
 * role can still invoke it directly. That gap is out of this story's file scope (scenario.ts ships
 * under the completed NX-2637, whose own TC5 test locks in a client-viewer getting a masked 200) —
 * flagged plainly in this story's report as AC7's `[eyes-on]` security finding, not fixed here.
 */
export const SCENARIO_LEVER_ROLES: readonly Role[] = ["admin", "delivery-lead", "sponsor"];
export const canRunScenarioLevers = (role: Role): boolean => SCENARIO_LEVER_ROLES.includes(role);

// --- the service --------------------------------------------------------------------------------

export interface ScenarioService {
  run(
    projectId: string,
    leverIds: readonly string[],
    token: string | null,
    trade?: { readonly scopeItemIds?: readonly string[]; readonly targetDate?: string },
  ): Promise<Envelope<ScenarioView>>;
}

export const liveScenarioService: ScenarioService = {
  run: (projectId, leverIds, token, trade) =>
    apiPost<ScenarioView>(`/projects/${projectId}/scenarios`, {
      token,
      body: { leverIds, ...trade },
    }),
};
